import { useState } from 'react';

import { type TabOption, Tabs } from '@/components/chariot-ui';
import { Button } from '@/components/ui/button';
import { useGlobalState } from '@/state/global.state';
import type { Hunt } from '@/types';
import { formatDate } from '@/utils/date.util';

import { DeleteConfirm } from './DeleteConfirm';
import { HuntStatusChip } from './HuntStatusChip';
import { ChatPane } from './panes/ChatPane';
import { FindingsPane } from './panes/FindingsPane';
import { LogPane } from './panes/LogPane';
import { MemoryPane } from './panes/MemoryPane';
import { OverviewPane } from './panes/OverviewPane';
import { useDeleteHunt, useHunts, useUpdateHuntStatus } from './useHunts';

const TABS: TabOption[] = [
  { label: 'Overview', value: 'overview' },
  { label: 'Log', value: 'log' },
  { label: 'Findings', value: 'findings' },
  { label: 'Memory', value: 'memory' },
  { label: 'Chat', value: 'chat' },
];

/**
 * HuntDetailDrawer renders inside the app-level Drawer wrapper mounted
 * in AppDrawers. The hunt UUID comes from global state (huntDrawerKey)
 * and we look it up in the live `useHunts()` list so sweep-driven
 * updates surface without a manual refresh.
 *
 * Returns null when no key is set — AppDrawers already gates on the
 * key, so this is a defensive fallback.
 */
export function HuntDetailDrawer() {
  const {
    drawerState: { huntDrawerKey, handleOpenHuntDrawer },
  } = useGlobalState();
  const { data: hunts = [] } = useHunts();
  const hunt = hunts.find(h => h.uuid === huntDrawerKey) ?? null;
  const [tab, setTab] = useState<TabOption>(TABS[0]);

  if (!hunt) return null;

  return (
    <div className="@container flex h-full flex-col">
      <DrawerHeader hunt={hunt} onClose={() => handleOpenHuntDrawer()} />
      <Tabs
        tabs={TABS}
        selected={tab}
        onSelect={setTab}
        variant="primary"
        enableScroll
      />
      <div className="flex-1 overflow-y-auto">
        {tab.value === 'overview' && <OverviewPane hunt={hunt} />}
        {tab.value === 'log' && <LogPane hunt={hunt} />}
        {tab.value === 'findings' && <FindingsPane hunt={hunt} />}
        {tab.value === 'memory' && <MemoryPane hunt={hunt} />}
        {tab.value === 'chat' && <ChatPane hunt={hunt} />}
      </div>
    </div>
  );
}

interface DrawerHeaderProps {
  hunt: Hunt;
  onClose: () => void;
}

function DrawerHeader({ hunt, onClose }: DrawerHeaderProps) {
  const { mutate: updateStatus, isPending: statusPending } =
    useUpdateHuntStatus();
  const { mutate: deleteHunt, isPending: deletePending } = useDeleteHunt();
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  return (
    <div className="border-chariot-stroke flex flex-col gap-2 border-b p-4 @md:flex-row @md:items-start @md:justify-between">
      <div className="min-w-0 flex-1 @md:pr-4">
        <div className="flex min-w-0 items-center gap-2">
          <h3 className="text-chariot-text-primary truncate text-base font-semibold">
            Persistent hunt
          </h3>
          <HuntStatusChip status={hunt.status} />
        </div>
        <div className="text-chariot-text-secondary mt-1 text-xs">
          Created {formatDate(hunt.created)} by {hunt.createdBy}
        </div>
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        {hunt.status === 'active' && (
          <Button
            size="sm"
            variant="outline"
            disabled={statusPending}
            onClick={() => updateStatus({ uuid: hunt.uuid, status: 'paused' })}
          >
            Pause
          </Button>
        )}
        {hunt.status === 'paused' && (
          <Button
            size="sm"
            disabled={statusPending}
            onClick={() => updateStatus({ uuid: hunt.uuid, status: 'active' })}
          >
            Resume
          </Button>
        )}
        <Button
          size="sm"
          variant="destructive"
          disabled={deletePending}
          onClick={() => setConfirmDeleteOpen(true)}
        >
          Delete
        </Button>
      </div>
      <DeleteConfirm
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title="Delete this hunt?"
        description="Its memory and findings remain but no more iterations will run."
        confirmLabel="Delete hunt"
        isPending={deletePending}
        onConfirm={() =>
          deleteHunt(
            { uuid: hunt.uuid },
            {
              onSuccess: () => {
                setConfirmDeleteOpen(false);
                onClose();
              },
            }
          )
        }
      />
    </div>
  );
}
